/**
 * Contentful SEO configuration precedence, shared by every brand.
 *
 * A page carries two entries: `seoConfiguration` (the shared, reusable one) and
 * `seoConfigurationOverride`. The override is authored to beat the base on the
 * fields it fills — not to replace the whole entry. Picking one entry wholesale
 * (`override ?? base`) drops every field the override left empty, so an override
 * that only sets a title silently removes the base description, canonical and
 * thumbnail.
 *
 * This merges per field, matching the legacy `breitling-storefront-next`
 * `SeoConfigBuilder` contract: `override.field ?? base.field`. `??` and not `||`,
 * so an intentionally empty string in the override still wins.
 *
 * Deliberately brand-agnostic and untyped: it merges the raw Contentful entries
 * before each brand's `adaptSeoConfiguration` runs, so it works whatever field
 * names a brand's content model uses.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function mergeSeoConfiguration(base: unknown, override: unknown): unknown {
  if (!isRecord(override)) return isRecord(base) ? base : (base ?? null);
  if (!isRecord(base)) return override;

  const merged: Record<string, unknown> = { ...base };

  for (const [field, value] of Object.entries(override)) {
    if (value !== null && value !== undefined) {
      merged[field] = value;
    }
  }

  return merged;
}
